import { useEffect, useState } from 'react';
import { useIcibAdmin } from '../context/IcibAdminContext';
import { useDrawer } from '../../drawer/DrawerContext';
import { useToast } from '../../toast/ToastContext';
import { fmtDT } from '../utils/format';
import Pagination, { usePagination } from '../components/Pagination';

/* ICIB-09 — Manage employer client groups (EmployerClient). */
function GroupsView() {
  const { db, status, createGroup, updateGroup, deactivateGroup, reactivateGroup } = useIcibAdmin();
  const { openDrawer, closeDrawer } = useDrawer();
  const toast = useToast();
  const [form, setForm] = useState({ name: '', company_code: '' });
  const [busy, setBusy] = useState(false);
  const { page, setPage, pageCount, pageItems: pagedGroups, total } = usePagination(db.groups);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    const res = await createGroup(form);
    setBusy(false);
    if (res?.error) { toast(res.message || 'Could not create this company.', 'bad', res.error); return; }
    toast(`${res.name} created.`, 'ok');
    setForm({ name: '', company_code: '' });
  }

  async function onDeactivate(g) {
    if (!window.confirm(`Deactivate ${g.name}? Its employer admins keep their accounts, but the scheme stops accepting new intake.`)) return;
    const res = await deactivateGroup(g.id);
    if (res?.error) toast(res.message || 'Could not deactivate this company.', 'bad', res.error);
    else { toast(`${g.name} deactivated.`, 'ok'); closeDrawer(); }
  }

  // ICIB-09f — undoes onDeactivate above.
  async function onReactivate(g) {
    const res = await reactivateGroup(g.id);
    if (res?.error) toast(res.message || 'Could not reactivate this company.', 'bad', res.error);
    else { toast(`${g.name} reactivated.`, 'ok'); closeDrawer(); }
  }

  function open(g) {
    openDrawer(g.name, `ICIB-09c · ${g.company_code}`, <GroupDrawer g={g} updateGroup={updateGroup} onDeactivate={onDeactivate} onReactivate={onReactivate} toast={toast} />);
  }

  if (status === 'loading') return <div className="card"><div className="empty"><b>Loading…</b></div></div>;

  return (
    <>
      <div className="eyebrow">Employer clients</div>
      <div className="page-h"><h2>Companies</h2></div>
      <p className="page-sub">Every employer ICIB administers a scheme for.</p>

      <div className="grid g2">
        <div>
          <div className="sec-h" style={{ marginTop: 0 }}><h3>All companies</h3><span className="spacer" /><span className="ep">GET /companies</span></div>
          {db.errors?.groups && <div className="note bad" style={{ marginBottom: 10 }}><b>Couldn't load companies</b>{db.errors.groups}</div>}
          <div className="card">
            {db.groups.length ? (
              <div className="tbl-wrap">
                <table>
                  <thead><tr><th>Company</th><th>Code</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {pagedGroups.map((g) => (
                      <tr className="clickable" key={g.id} onClick={() => open(g)}>
                        <td className="strong">{g.name}</td>
                        <td className="mono">{g.company_code}</td>
                        <td><span className={'tag' + (g.status === 'active' ? ' ok' : ' off')}>{g.status}</span></td>
                        <td><button className="btn sm" onClick={(e) => { e.stopPropagation(); open(g); }}>Open</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div className="empty"><b>No companies yet</b>Create the first one on the right.</div>}
            <Pagination page={page} setPage={setPage} pageCount={pageCount} total={total} />
          </div>
        </div>

        <div>
          <div className="sec-h" style={{ marginTop: 0 }}><h3>New company</h3><span className="spacer" /><span className="ep">POST /companies</span></div>
          <div className="card card-b">
            <form onSubmit={submit}>
              <div className="field">
                <label>Name <span className="req">*</span></label>
                <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Pacific Radiology" />
              </div>
              <div className="field">
                <label>Company code <span className="req">*</span></label>
                <input required value={form.company_code} onChange={(e) => setForm((f) => ({ ...f, company_code: e.target.value.toUpperCase() }))} placeholder="PACRAD" />
                <div className="hint">Used on CSV templates instead of the internal id — must be unique.</div>
              </div>
              <div className="btn-row"><button type="submit" className="btn pri" disabled={busy}>{busy ? 'Creating…' : 'Create company'}</button></div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

function GroupDrawer({ g, updateGroup, onDeactivate, onReactivate, toast }) {
  const { refresh } = useIcibAdmin();
  const [form, setForm] = useState({ name: g.name, company_code: g.company_code });
  const [busy, setBusy] = useState(false);
  // ICIB-09c — set when the server says this company no longer exists (e.g.
  // deleted after the list loaded). Shows the server's message instead of an
  // endless "Loading…" and reloads the list so the stale row disappears.
  const [loadError, setLoadError] = useState(null);

  function onLoadError(res) {
    setLoadError(res);
    if (res?.error === 'not_found') refresh();
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    const res = await updateGroup(g.id, form);
    setBusy(false);
    if (res?.error) toast(res.message || 'Could not save this company.', 'bad', res.error);
    else toast('Company updated.', 'ok');
  }

  if (loadError) {
    return <div className="note bad"><b>{loadError.error === 'not_found' ? 'This company no longer exists' : "Couldn't load this company"}</b>{loadError.message || 'Please reload the list.'}</div>;
  }

  return (
    <form onSubmit={submit}>
      <div className="field">
        <label>Name</label>
        <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
      </div>
      <div className="field">
        <label>Company code</label>
        <input value={form.company_code} onChange={(e) => setForm((f) => ({ ...f, company_code: e.target.value.toUpperCase() }))} />
        <div className="hint">Changing this invalidates any already-downloaded CSV templates carrying the old code.</div>
      </div>
      <div className="btn-row">
        <button type="submit" className="btn pri" disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button>
        {g.status === 'active'
          ? <button type="button" className="btn danger" onClick={() => onDeactivate(g)}>Deactivate</button>
          : <button type="button" className="btn" onClick={() => onReactivate(g)}>Reactivate</button>}
      </div>

      <div className="sec-h"><h3>Employer admins</h3><span className="spacer" /><span className="ep">GET /companies/{'{company_id}'}/employer-admins</span></div>
      <GroupAdminsList gid={g.id} onError={onLoadError} />
    </form>
  );
}

// ICIB-08i
function GroupAdminsList({ gid, onError }) {
  const { groupAdmins, loadGroupAdmins } = useIcibAdmin();
  // loadGroupAdmins isn't memoized in the provider — key off gid alone so
  // the effect doesn't refire on every render once its own setState lands.
  useEffect(() => {
    let live = true;
    loadGroupAdmins(gid).then((res) => { if (live && res?.error) onError?.(res); });
    return () => { live = false; };
  }, [gid]); // eslint-disable-line react-hooks/exhaustive-deps
  const data = groupAdmins[gid];
  const admins = data?.employer_admins || [];
  const adminsPage = usePagination(admins);

  if (!data) return <div className="empty"><b>Loading…</b></div>;
  if (!admins.length) return <div className="empty"><b>No employer admins assigned</b>Add one from User accounts.</div>;

  return (
    <>
      <div className="tbl-wrap">
        <table>
          <thead><tr><th>Name</th><th>Job title</th><th>Assigned</th></tr></thead>
          <tbody>
            {adminsPage.pageItems.map((a) => (
              <tr key={a.id}>
                <td><span className="strong">{a.first_name} {a.last_name}</span><br /><span className="mono dim">{a.email}</span></td>
                <td>{a.job_title || <span className="dim">—</span>}</td>
                <td className="mono dim">{fmtDT(a.assigned_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination {...adminsPage} />
    </>
  );
}

export default GroupsView;
